import { defineStore } from 'pinia'
import { jwtDecode } from 'jwt-decode'
import { type UserJWT, UserRole, rolePriority } from '@/types/jwt'
import { CurrencyCodeByCountry } from '@/utils/constants/country'
import type { StoreGetDataWithCurrency, StoreGetStoreResponse } from '@/types/redemption'
import { getCookieDomain } from '@/utils/helper/getCookieDomain'
import { computed, ref, piniaPluginPersistedstate } from '#imports'
import { useClientStoreRepository } from '@/composables/api/repositories/client-store'

export const useUserStore = defineStore(
  'user',
  () => {
    const clientStoreApi = useClientStoreRepository()

    const userToken = ref<string>('')
    const impersonationToken = ref<string>('')
    const setImpersonationToken = (token: string): void => {
      impersonationToken.value = token
    }

    const user = ref<UserJWT | null>()
    const roles = ref<UserRole[]>()
    const currentRole = ref<UserRole | null>(null)
    const store = ref<StoreGetDataWithCurrency | null>(null)
    const loading = ref<boolean>(false)

    const assignRole = (): UserRole => {
      if (!roles.value || roles.value.length === 0) {
        return UserRole.Employee
      }

      if (roles.value.length === 1) {
        return roles.value[0]
      }

      for (const role of rolePriority) {
        if (roles.value.includes(role)) {
          return role
        }
      }

      return UserRole.Employee
    }

    const setUser = (token: string): void => {
      userToken.value = token
      user.value = jwtDecode<UserJWT>(userToken.value)
      roles.value = user.value?.roles ?? []
      currentRole.value = assignRole()
    }

    const setStore = async (newStore: StoreGetStoreResponse): Promise<void> => {
      if (newStore) {
        store.value = newStore
        const partnerId = newStore.partnerId?.split('.')
        store.value.currencyCode = CurrencyCodeByCountry[partnerId?.[2] as keyof typeof CurrencyCodeByCountry]?.currencyCode ?? CurrencyCodeByCountry.us.currencyCode
        impersonationToken.value = ''
      }
    }

    const initials = computed<string>(() =>
      user.value?.firstName && user.value?.lastName
        ? `${user.value.firstName[0]}${user.value.lastName[0]}`
        : '',
    )

    const logout = (): void => {
      userToken.value = ''
      store.value = null
      roles.value = []
      currentRole.value = null
      user.value = null
      loading.value = false
    }

    const updateStore = async (storeId: string): Promise<void> => {
      if (user.value && storeId) {
        try {
          const response = await clientStoreApi.execute<'switchStore'>(
            clientStoreApi.switchStore(storeId),
          )

          setUser(response.token)
          setStore({
            storeId: response.storeId,
            storeCode: response.storeCode,
            storeName: response.storeName,
            partnerId: response.partnerId,
          })
        } catch (error) {
          console.error('[updateStore]: Failed to switch store', error)
          throw error
        }
      }
    }

    const isAMPortalUser = computed<boolean>(
      () => currentRole.value === UserRole.AMPortalUser,
    )
    const isEmployer = computed<boolean>(
      () => currentRole.value === UserRole.Employer,
    )
    const isEmployee = computed<boolean>(
      () => currentRole.value === UserRole.Employee,
    )
    const isAllowed = computed<boolean>(
      () => isAMPortalUser.value || isEmployer.value,
    )

    return {
      loading,
      userToken,
      impersonationToken,
      setImpersonationToken,
      user,
      roles,
      currentRole,
      initials,
      store,
      isAMPortalUser,
      isEmployer,
      isEmployee,
      isAllowed,
      setUser,
      setStore,
      updateStore,
      logout,
    }
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.cookies({
        sameSite: 'strict',
        domain: getCookieDomain(),
        secure: process.env.NODE_ENV === 'production',
        maxAge: 3600, // 1 hour
      }),
      pick: [
        'userToken',
        'user',
        'roles',
        'currentRole',
        'initials',
        'store',
        'impersonationToken',
      ],
    },
  },
)
